import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, User, Bot } from 'lucide-react';
interface RiskTuningChatProps {
  weights: Record<string, number>;
  onWeightsChange: (weights: Record<string, number>) => void;
}
interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  proposedWeights?: Record<string, number>;
}
const PRESETS = [{
  label: 'Interior Renovation',
  prompt: "We mostly do interior renovation. Weather shouldn't matter as much.",
  weights: {
    visual_hazards: 45,
    human_factors: 30,
    operational_friction: 20,
    documentation_quality: 5
  },
  explanation: "For interior work, I've reduced the Weather/Environmental impact significantly. I've redistributed that weight to Visual Hazards and Human Factors, since housekeeping and worker fatigue are typically the primary drivers of interior incidents."
}, {
  label: 'High-Rise Construction',
  prompt: 'We are doing high-rise new construction. Fall protection and wind are huge concerns.',
  weights: {
    visual_hazards: 50,
    human_factors: 20,
    operational_friction: 15,
    documentation_quality: 15
  },
  explanation: "I've increased the Visual Hazards weight to 50% to heavily penalize missing fall protection or leading edge issues. I've kept documentation quality steady to ensure crane and hoist daily inspections are prioritized."
}, {
  label: 'High Turnover / New Crews',
  prompt: 'We have a lot of new workers and high turnover right now.',
  weights: {
    visual_hazards: 30,
    human_factors: 45,
    operational_friction: 15,
    documentation_quality: 10
  },
  explanation: "I've shifted the primary focus to Human Factors (45%). This means the model will be much more sensitive to crews with less than 30 days of experience or workers logging excessive overtime."
}];
export function RiskTuningChat({
  weights,
  onWeightsChange
}: RiskTuningChatProps) {
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: 'welcome',
    role: 'assistant',
    text: 'Hi! I can help you tune your risk model to match your specific site conditions or safety priorities. Tell me about your project type, or what risks keep you up at night.'
  }]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  const handleSend = (text: string, preset?: (typeof PRESETS)[0]) => {
    if (!text.trim()) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      text
    };
    setMessages(prev => [...prev, newUserMsg]);
    setQuery('');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      let responseText = '';
      let newWeights = {
        ...weights
      };
      if (preset) {
        responseText = preset.explanation;
        newWeights = preset.weights;
      } else {
        // Generic fallback for custom text
        const lowerText = text.toLowerCase();
        if (lowerText.includes('weather') || lowerText.includes('rain') || lowerText.includes('wind')) {
          responseText = "I understand weather is a key factor for you. I've adjusted the model to be more sensitive to environmental conditions by shifting weight from documentation to visual/environmental hazards.";
          newWeights = {
            visual_hazards: 50,
            human_factors: 20,
            operational_friction: 20,
            documentation_quality: 10
          };
        } else if (lowerText.includes('paperwork') || lowerText.includes('ptp') || lowerText.includes('jha')) {
          responseText = "Got it. If documentation compliance is your main leading indicator, I've increased its weight significantly. The model will now flag sites faster if PTPs are missing or generic.";
          newWeights = {
            visual_hazards: 30,
            human_factors: 20,
            operational_friction: 15,
            documentation_quality: 35
          };
        } else {
          responseText = "Based on what you shared, I've slightly adjusted the weights to prioritize human factors and visual hazards, which are the most common drivers for that profile.";
          newWeights = {
            visual_hazards: 45,
            human_factors: 30,
            operational_friction: 15,
            documentation_quality: 10
          };
        }
      }
      const newAiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: responseText,
        proposedWeights: newWeights
      };
      setMessages(prev => [...prev, newAiMsg]);
      onWeightsChange(newWeights);
    }, 1500);
  };
  return <div className="flex flex-col h-full bg-gray-50 rounded-xl border border-gray-200 overflow-hidden" data-id="element-3749">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-3750">
        {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-3751">
            <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm'}`} data-id="element-3752">
              {msg.role === 'assistant' && <div className="flex items-center gap-1.5 mb-1.5 text-indigo-600" data-id="element-3753">
                  <Sparkles size={12} data-id="element-3754" />
                  <span className="text-xs font-bold uppercase tracking-wider" data-id="element-3755">
                    Bilton AI Advisor
                  </span>
                </div>}
              <div className="whitespace-pre-wrap leading-relaxed" data-id="element-3756">
                {msg.text}
              </div>

              {/* Show weight changes if proposed */}
              {msg.proposedWeights && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-3757">
                  <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider" data-id="element-3758">
                    Applied Weights:
                  </p>
                  <div className="grid grid-cols-2 gap-2" data-id="element-3759">
                    <div className="bg-gray-50 p-2 rounded border border-gray-100" data-id="element-3760">
                      <div className="text-[10px] text-gray-500 truncate" data-id="element-3761">
                        Visual Hazards
                      </div>
                      <div className="font-bold text-indigo-600" data-id="element-3762">
                        {msg.proposedWeights.visual_hazards}%
                      </div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded border border-gray-100" data-id="element-3763">
                      <div className="text-[10px] text-gray-500 truncate" data-id="element-3764">
                        Human Factors
                      </div>
                      <div className="font-bold text-indigo-600" data-id="element-3765">
                        {msg.proposedWeights.human_factors}%
                      </div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded border border-gray-100" data-id="element-3766">
                      <div className="text-[10px] text-gray-500 truncate" data-id="element-3767">
                        Operational Friction
                      </div>
                      <div className="font-bold text-indigo-600" data-id="element-3768">
                        {msg.proposedWeights.operational_friction}%
                      </div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded border border-gray-100" data-id="element-3769">
                      <div className="text-[10px] text-gray-500 truncate" data-id="element-3770">
                        Documentation
                      </div>
                      <div className="font-bold text-indigo-600" data-id="element-3771">
                        {msg.proposedWeights.documentation_quality}%
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>)}

        {isTyping && <div className="flex justify-start" data-id="element-3772">
            <div className="bg-white border border-gray-200 p-4 rounded-2xl rounded-tl-sm shadow-sm flex gap-1" data-id="element-3773">
              <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" data-id="element-3774" />
              <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
            animationDelay: '0.2s'
          }} data-id="element-3775" />
              <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
            animationDelay: '0.4s'
          }} data-id="element-3776" />
            </div>
          </div>}
        <div ref={messagesEndRef} data-id="element-3777" />
      </div>

      {/* Input Area */}
      <div className="p-4 bg-white border-t border-gray-200" data-id="element-3778">
        {/* Quick Prompts */}
        <div className="flex flex-wrap gap-2 mb-3" data-id="element-3779">
          {PRESETS.map((preset, idx) => <button key={idx} onClick={() => handleSend(preset.prompt, preset)} disabled={isTyping} className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full transition-colors whitespace-nowrap disabled:opacity-50" data-id="element-3780">
              {preset.label}
            </button>)}
        </div>

        <div className="relative" data-id="element-3781">
          <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend(query)} placeholder="Describe your site conditions or priorities..." disabled={isTyping} className="w-full pl-4 pr-12 py-3 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow disabled:opacity-50" data-id="element-3782" />
          <button onClick={() => handleSend(query)} disabled={!query.trim() || isTyping} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-colors" data-id="element-3783">
            <Send size={16} data-id="element-3784" />
          </button>
        </div>
      </div>
    </div>;
}